import moment from 'moment';

/**
 * SearchContext builder — normalizes the live search state into the
 * `SearchContext` shape the rate-gate service expects (see
 * notes/public-rate-gate-structs-P1.md). Pure: takes plain slices, returns a
 * plain object. Used by both the unlock request (rate_gate_actions) and the
 * public booking payload (util/publicBookingPayload).
 */

/** @readonly @enum {string} Shipment mode enum (never compare raw strings). */
export const Mode = Object.freeze({
  FCL: 'FCL',
  LCL: 'LCL',
  AIR: 'AIR',
});

/** queryParams.type → Mode. Unknown/missing types fall back to FCL (the app default). */
const TYPE_TO_MODE = Object.freeze({
  'api-fcl': Mode.FCL,
  'api-lcl': Mode.LCL,
  'api-air': Mode.AIR,
});

/** The app's queryParams date format ("18 Jul, 2026"). */
const QUERY_PARAMS_DATE_FORMAT = 'DD MMM, YYYY';

const toPortRef = (port) => {
  if (!port || typeof port !== 'object') return null;
  return {
    id: port.id != null ? port.id : null,
    name: port.name || port.city || null,
    city: port.city || null,
    country: port.country || null,
    portCode: port.portCode || null,
  };
};

const toIsoDate = (date) => {
  if (!date) return null;
  const parsed = moment(date, [QUERY_PARAMS_DATE_FORMAT, moment.ISO_8601], true);
  if (parsed.isValid()) return parsed.format('YYYY-MM-DD');
  const loose = moment(new Date(date));
  return loose.isValid() ? loose.format('YYYY-MM-DD') : null;
};

const toContainers = (fclCounters) => {
  const counters = fclCounters || {};
  return {
    _20st: Number(counters._20stCounter) || 0,
    _40st: Number(counters._40stCounter) || 0,
    _40hq: Number(counters._40hqCounter) || 0,
    _45hq: Number(counters._45hqCounter) || 0,
  };
};

const toShipment = (shipment) => {
  if (!shipment || typeof shipment !== 'object') return null;
  return {
    quantity: shipment.quantity != null ? Number(shipment.quantity) : null,
    totalWeight: shipment.totalWeight != null ? Number(shipment.totalWeight) : null,
    weightUnit: shipment.weightUnit || null,
    totalVolume: shipment.totalVolume != null ? Number(shipment.totalVolume) : null,
    volumeUnit: shipment.volumeUnit || null,
  };
};

/**
 * @param {Object} input
 * @param {Object} input.queryParams   state.route.queryParams ({ from, to, date, type })
 * @param {Object} [input.fclCounters] state.code_config.fclCounters (FCL only)
 * @param {Object} [input.lclShipment] state.code_config.lclShipment (LCL only)
 * @param {Object} [input.airShipment] state.code_config.airShipment (AIR only)
 * @returns {Object} SearchContext — { mode, from, to, date, containers, shipment }
 */
export const buildSearchContext = ({
  queryParams,
  fclCounters,
  lclShipment,
  airShipment,
} = {}) => {
  const params = queryParams || {};
  const mode = TYPE_TO_MODE[params.type] || Mode.FCL;

  return {
    mode,
    from: toPortRef(params.from),
    to: toPortRef(params.to),
    date: toIsoDate(params.date),
    containers: mode === Mode.FCL ? toContainers(fclCounters) : null,
    shipment:
      mode === Mode.LCL
        ? toShipment(lclShipment)
        : mode === Mode.AIR
          ? toShipment(airShipment)
          : null,
  };
};
